'use client';
import MetaMaskSDK, { SDKProvider } from '@metamask/sdk';
import { ethers } from 'ethers';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { initializeMetaMaskSDK } from '@/configuration/metaMaskSdk';
import { initialMetaMaskSdk } from '@/pages/_app';

type MetaMaskContextType = {
  metaMaskSdk: MetaMaskSDK | null;
  onConnectMetaMask: () => Promise<void>;
  onDisconnect: () => Promise<void>;
  onSignMetaMask: (message: string) => Promise<string | null>;
  onSwitchMetaMaskNetwork: (chainId: string) => Promise<void>;
  getConnectedAccounts: () => Promise<string[] | undefined>;
  getConnectedChainId: () => Promise<number | undefined>;
  getMetaMaskProvider: () => ethers.providers.Web3Provider | undefined;
};

const MetaMaskContext = createContext<MetaMaskContextType | undefined>(undefined);

const MetaMaskProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [metaMaskSdk, setMetaMaskSdk] = useState<ReturnType<typeof initializeMetaMaskSDK> | null>(null);

  useEffect(() => {
    initialMetaMaskSdk
      .init()
      .then(() => {
        setMetaMaskSdk(initialMetaMaskSdk);
      })
      .catch(() => {
        throw new Error('Failed to initialize MetaMask SDK');
      });
  }, []);

  const getConnectedAccounts = useCallback(async () => {
    const ethereum = metaMaskSdk?.activeProvider || metaMaskSdk?.getProvider();

    if (!ethereum) {
      if (typeof window !== 'undefined' && !window.ethereum) {
        throw new Error('Please install MetaMask to use this feature.');
      }
      throw new Error('MetaMask provider not available');
    }

    try {
      const accounts = (await ethereum.request({
        method: 'eth_requestAccounts'
      })) as string[];

      return accounts;
    } catch (error) {
      throw error;
    }
  }, [metaMaskSdk]);

  const onDisconnect = useCallback(async () => {
    if (!metaMaskSdk) return;
    await metaMaskSdk.terminate();
  }, [metaMaskSdk]);

  const getConnectedChainId = useCallback(async () => {
    const ethereum = metaMaskSdk?.activeProvider || metaMaskSdk?.getProvider();

    if (!ethereum) {
      throw new Error('MetaMask provider not available');
    }

    try {
      const hexChainId = (await ethereum.request({
        method: 'eth_chainId'
      })) as string;

      return parseInt(hexChainId, 16);
    } catch (error) {
      throw error;
    }
  }, [metaMaskSdk]);

  const onConnectMetaMask = useCallback(async () => {
    const ethereum = metaMaskSdk?.activeProvider || metaMaskSdk?.getProvider();

    if (!ethereum) {
      throw new Error('MetaMask provider not available');
    }

    try {
      await ethereum.request({ method: 'eth_requestAccounts' });
    } catch (error) {
      throw error;
    }
  }, [metaMaskSdk]);

  const onSignMetaMask = useCallback(
    async (message: string): Promise<string | null> => {
      const ethereum = metaMaskSdk?.activeProvider || metaMaskSdk?.getProvider();

      if (!ethereum) {
        throw new Error('MetaMask provider not available');
      }

      try {
        const accounts = (await ethereum.request({
          method: 'eth_requestAccounts'
        })) as string[];
        const hexMessage = `0x${Buffer.from(message, 'utf8').toString('hex')}`;
        const from = accounts[0];

        const signature = (await ethereum.request({
          method: 'personal_sign',
          params: [hexMessage, from]
        })) as string;

        return signature;
      } catch (error) {
        throw error;
      }
    },
    [metaMaskSdk]
  );

  const onSwitchMetaMaskNetwork = useCallback(
    async (chainId: string) => {
      const ethereum = metaMaskSdk?.activeProvider || metaMaskSdk?.getProvider();

      if (!ethereum) {
        throw new Error('MetaMask provider not available');
      }

      try {
        await ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId }]
        });
      } catch (error) {
        throw error;
      }
    },
    [metaMaskSdk]
  );

  const getMetaMaskProvider = useCallback(() => {
    if (!metaMaskSdk) return;

    const provider =
      metaMaskSdk.activeProvider || (metaMaskSdk.getProvider() as SDKProvider | ethers.providers.Web3Provider);

    if (!provider) return;

    return new ethers.providers.Web3Provider(provider as any);
  }, [metaMaskSdk]);

  return (
    <MetaMaskContext.Provider
      value={{
        metaMaskSdk,
        onDisconnect,
        onSwitchMetaMaskNetwork,
        onConnectMetaMask,
        onSignMetaMask,
        getConnectedAccounts,
        getConnectedChainId,
        getMetaMaskProvider
      }}
    >
      {children}
    </MetaMaskContext.Provider>
  );
};

export const useMetaMask = (): MetaMaskContextType => {
  const context = useContext(MetaMaskContext);

  if (!context) {
    throw new Error('useMetaMask must be used within a MetaMaskProvider');
  }

  return context;
};

export default MetaMaskProvider;
